import * as React from 'react';
import { parse, stringify } from 'query-string';
import { isString, parseInt, uniqueId } from 'lodash';
import { domToReact, htmlToDOM } from 'html-react-parser';
import { NumberRange } from '@blueprintjs/core';

import Overlay from '../Overlay';
import Icon from '../Icon';
import CategoryTreeRange from './range';
import Button from '../Button';
import Buttons from '../Buttons';
import classNames from 'classnames';

export interface ICategoryTreeProps {
    /* DOM to parse */
    dom: string;
    /** URL query string */
    query: string;
    /** Open mobile menu event */
    openMobileFilter: (e) => void;
    /** Close mobile menu event */
    closeMobileFilter: (e) => void;
    /** Should mobile menu be open */
    mobileFilterOpen: boolean;
    /** React-router link component to use instead of HTML A tag. If defined href is passed as "to" prop. */
    link?: any;
    /** React-router history instance for url modifications (for slider) */
    history: any;
    /** React-router location instance for url modifications (for slider) */
    location: any;
    /** If true, category tree node hrefs don't include query parameters from url */
    keepOriginalHref?: boolean;
    /** Label for the filtering submit button */
    labelSubmit?: string;
    /** Layout to use */
    layout?: 'default' | 'tabs';
}

const CategoryTree: React.FunctionComponent<ICategoryTreeProps> = (props) => {
    const [drop, setDrop] = React.useState<React.ReactNode>(null);
    const [activeItem, setActiveItem] = React.useState<string>('');

    let Elem: any = 'a';
    if (props.link) {
        Elem = props.link;
    } else {
        Elem = 'a';
    }

    const q = parse(props.query);

    const parserOptions = {
        replace: (domNode: any) => {
            if (domNode.name) {
                const newId = uniqueId('cat-');

                if (domNode.name === 'input' && domNode.attribs.type === 'checkbox' && domNode.next && domNode.next.name === 'label') {
                    const { class: className, checked, id, ...rest } = domNode.attribs;
                    const { class: labelClassName, for: labelFor, ...labelRest } = domNode.next.attribs;
                    const inner = domToReact(domNode.next.children, parserOptions);
                    return (
                        <React.Fragment>
                            <input
                                {...rest}
                                id={newId}
                                type="checkbox"
                                defaultChecked={checked !== undefined}
                            />
                            <label
                                {...labelRest}
                                className={labelClassName}
                                htmlFor={newId}
                            >{inner}</label>
                        </React.Fragment>
                    );
                }

                if (domNode.name === 'label' && domNode.prev && domNode.prev.name === 'input') {
                    return (
                        <React.Fragment/>
                    );
                }

                if (domNode.name === 'ul') {
                    const inner = domToReact(domNode.children, parserOptions);
                    const { class: className, href, ...rest } = domNode.attribs;
                    return (<ul className={className}>{inner}</ul>);
                }

                if (domNode.name === 'li') {
                    const inner = domToReact(domNode.children, parserOptions);
                    const { class: className, href, ...rest } = domNode.attribs;
                    return (<li className={className}>{inner}</li>);
                }

                if (domNode.name === 'label' && !domNode.prev) {
                    const inner = domToReact(domNode.children, parserOptions);
                    const { class: className, href, ...rest } = domNode.attribs;
                    const isActive = className?.split(' ').includes('active') || false;
                    return (
                        <React.Fragment>
                            <input
                                type="checkbox"
                                defaultChecked={isActive}
                                id={newId}
                            />
                            <div className="item-row">
                                <label htmlFor={newId} className={className}>{inner}</label>
                            </div>
                        </React.Fragment>
                    );
                }

                if (domNode.name === 'a' && !!domNode.next) {
                    const inner = domToReact(domNode.children, parserOptions);
                    const { class: className, href, ...rest } = domNode.attribs;
                    const isMenuToggler = className?.split(' ').includes('menutoggler') || false;
                    const isActive = className?.split(' ').includes('active') || false;

                    const cleanHref = domNode.attribs.href.split(/[?#]/)[0] || '';
                    const k = domNode.attribs['data-key'];
                    const v = domNode.attribs['data-value'];
                    const newQuery = stringify({
                        ...q,
                        ...(k ? { [k]: v, } : {}),
                    });
                    const newHref = props.keepOriginalHref ? cleanHref : cleanHref + '?' + newQuery;

                    if (isMenuToggler) {
                        return (
                            <a
                                {...rest}
                                href="#"
                                className={className}
                                onClick={props.openMobileFilter}
                            >{inner}</a>
                        );
                    }

                    if (!domNode.prev) {
                        return (
                            <React.Fragment>
                                <input
                                    type="checkbox"
                                    defaultChecked={isActive}
                                    id={newId}
                                />
                                <div className="item-row">
                                    <Elem
                                        {...rest}
                                        {...(props.link ? { to: newHref, } : { href: newHref, })}
                                        className={className}
                                    >{inner}</Elem>
                                    <label htmlFor={newId}/>
                                </div>
                            </React.Fragment>
                        );
                    }
                }

                if (domNode.name === 'a') {
                    const inner = domToReact(domNode.children, parserOptions);
                    const { class: className, href, ...rest } = domNode.attribs;
                    const cleanHref = domNode.attribs.href.split(/[?#]/)[0] || '';
                    const k = domNode.attribs['data-key'];
                    const v = domNode.attribs['data-value'];
                    const newQuery = stringify({
                        ...q,
                        ...(k ? { [k]: v, } : {}),
                    });
                    const newHref = props.keepOriginalHref ? cleanHref : cleanHref + '?' + newQuery;
                    const isFilter = !!k;

                    // className={classNames(filter(className, (x) => x === 'active'), { active: isActive, })}
                    // isActive={() => false}

                    return (
                        <Elem
                            {...rest}
                            {...(props.link ? {
                                to: newHref,
                                ...(isFilter ? {
                                    isActive: () => {
                                        let isActive = false;
                                        if (q[k] && v && q[k] === v) {
                                            isActive = true;
                                        }
                                        return isActive;
                                    },
                                } : {}),
                            } : { href: newHref, })}
                            className={className}
                        >{inner}</Elem>
                    );
                }

                if (domNode.name === 'div') {
                    const { class: className, checked, id, ...rest } = domNode.attribs;
                    const isRangeSelect = className?.split(' ').includes('range-select') || false;

                    if (isRangeSelect) {
                        const min = parseInt(rest['data-min'] || 0);
                        const max = parseInt(rest['data-max'] || 0);
                        let value: NumberRange = [min, max];
                        const k = domNode.attribs['data-key'];
                        const rawValueFromQ = q[k];
                        const rawValue = rest['data-value'];

                        const handleRawValue = (v: string): NumberRange => {
                            const x = v.split('-');
                            if (x.length === 2) {
                                const maybeMin = parseInt(x[0]);
                                const maybeMax = parseInt(x[1]);

                                if (isNaN(maybeMin) || isNaN(maybeMax)) {
                                    return [min, max];
                                }

                                if (maybeMin < min) {
                                    return [min, max];
                                }

                                if (maybeMax > max) {
                                    return [min, max];
                                }

                                return ([maybeMin, maybeMax]);
                            }
                            return [min, max];
                        };

                        if (rawValueFromQ && isString(rawValueFromQ)) {
                            value = handleRawValue(rawValueFromQ);
                        }

                        return (
                            <div>
                                <CategoryTreeRange
                                    min={min}
                                    max={max}
                                    value={value}
                                    stepSize={Math.ceil((max - min) / 200)}
                                    labelStepSize={max}
                                    onRelease={(range) => {
                                        const cleanHref = location.pathname.split(/[?#]/)[0] || '';
                                        const newQuery = stringify({
                                            ...q,
                                            ...(k ? { [k]: range.join('-'), } : {}),
                                        });
                                        const newHref = cleanHref + '?' + newQuery;
                                        props.history.push(newHref);
                                    }}
                                />
                            </div>
                        );
                    }
                }
            }
            return false;
        },
    };

    const parserOptionsTabs = {
        replace: (domNode: any) => {
            if (domNode.name) {
                const newId = uniqueId('cat-');

                if (domNode.name === 'input' && domNode.attribs.type === 'checkbox' && domNode.next && domNode.next.name === 'label') {
                    return (
                        <React.Fragment />
                    );
                }

                if (domNode.name === 'label' && domNode.prev && domNode.prev.name === 'input' && domNode.next && domNode.next.name === 'ul') {
                    const { class: className, for: labelFor, id, ...rest } = domNode.attribs;

                    const inner = domToReact(domNode.children, parserOptions);
                    const ul = domToReact(domNode.next.children, parserOptions);
                    return (
                        <label
                            className={classNames({ active: activeItem === labelFor, })}
                            onClick={(e) => {
                                e.preventDefault();
                                if (activeItem === labelFor) {
                                    setActiveItem('');
                                    setDrop(null);
                                } else {
                                    setActiveItem(labelFor);
                                    setDrop(ul);
                                }
                            }}
                        >
                            {inner}
                        </label>
                    );
                }

                if (domNode.name === 'label' && !domNode.prev) {
                    const inner = domToReact(domNode.children, parserOptions);
                    const { class: className, href, ...rest } = domNode.attribs;
                    const isActive = className?.split(' ').includes('active') || false;
                    return (
                        <div className="item-row">
                            <label className={className}>B {inner}</label>
                        </div>
                    );
                }

                if (domNode.name === 'a' && !!domNode.next) {
                    const inner = domToReact(domNode.children, parserOptions);
                    const { class: className, href, ...rest } = domNode.attribs;
                    const isMenuToggler = className?.split(' ').includes('menutoggler') || false;
                    const isActive = className?.split(' ').includes('active') || false;

                    const cleanHref = domNode.attribs.href.split(/[?#]/)[0] || '';
                    const k = domNode.attribs['data-key'];
                    const v = domNode.attribs['data-value'];
                    const newQuery = stringify({
                        ...q,
                        ...(k ? { [k]: v, } : {}),
                    });
                    const newHref = props.keepOriginalHref ? cleanHref : cleanHref + '?' + newQuery;

                    if (isMenuToggler) {
                        return (
                            <a
                                {...rest}
                                href="#"
                                className={className}
                                onClick={props.openMobileFilter}
                            >
                                {inner}
                            </a>
                        );
                    }

                    if (!domNode.prev) {
                        return (
                            <React.Fragment>
                                <div className="item-row">
                                    <Elem
                                        {...rest}
                                        {...(props.link ? { to: newHref, } : { href: newHref, })}
                                        className={className}
                                    >{inner}</Elem>
                                    <label htmlFor={newId}/>
                                </div>
                            </React.Fragment>
                        );
                    }
                }

                if (domNode.name === 'a') {
                    const inner = domToReact(domNode.children, parserOptions);
                    const { class: className, href, ...rest } = domNode.attribs;
                    const cleanHref = domNode.attribs.href.split(/[?#]/)[0] || '';
                    const k = domNode.attribs['data-key'];
                    const v = domNode.attribs['data-value'];
                    const newQuery = stringify({
                        ...q,
                        ...(k ? { [k]: v, } : {}),
                    });
                    const newHref = props.keepOriginalHref ? cleanHref : cleanHref + '?' + newQuery;
                    const isFilter = !!k;

                    return (
                        <Elem
                            {...rest}
                            {...(props.link ? {
                                to: newHref,
                                ...(isFilter ? {
                                    isActive: () => {
                                        let isActive = false;
                                        if (q[k] && v && q[k] === v) {
                                            isActive = true;
                                        }
                                        return isActive;
                                    },
                                } : {}),
                            } : { href: newHref, })}
                            className={className}
                        >
                            {inner}
                        </Elem>
                    );
                }
            }

            if (domNode.name === 'ul') {
                return (
                    <React.Fragment />
                );
            }
            return false;
        },
    };

    // Main output

    const domTree = htmlToDOM(props.dom.replace(/>\s+</g, '><'));

    return (
        <React.Fragment>
            {props.layout && props.layout === 'tabs' ? (
                <React.Fragment>
                    {domToReact(domTree, parserOptionsTabs)}
                    {!!drop && (
                        <div className="category-tree__drop">
                            <ul>
                                {drop}
                            </ul>
                        </div>
                    )}
                </React.Fragment>
            ) : (
                domToReact(domTree, parserOptions)
            )}
            <Overlay
                isOpen={props.mobileFilterOpen}
                doClose={props.closeMobileFilter}
                layout="category-tree"
                className="category-tree__mobile"
                customHeader={(
                    <div className="category-tree__mobile__heading">
                        <button onClick={props.closeMobileFilter}>
                            <Icon kind="close" width={16} height={16}/>
                        </button>
                        {props.labelSubmit && (
                            <Buttons layout="vertical">
                                <Button
                                    title={props.labelSubmit}
                                    intent="primary"
                                    onClick={props.closeMobileFilter}
                                />
                            </Buttons>
                        )}
                    </div>
                )}
            >
                <div className="category-tree__mobile__content">
                    {domToReact(domTree, parserOptions)}
                </div>
            </Overlay>
        </React.Fragment>
    );
};

CategoryTree.displayName = 'CategoryTree';

export default CategoryTree;
